import React, { FC, forwardRef, ReactNode, useRef, useState } from 'react';
import {
  Menu,
  MenuProps,
  Collapse,
  useMediaQuery,
  Theme,
  useTheme,
  CollapseProps,
  ClickAwayListener,
  ClickAwayListenerProps,
} from '@mui/material';
import { ChevronDownIcon } from '@/src/components/icon/stroke/ChevronDownIcon';
import { HeaderNavigationLink, HeaderNavigationLinkProps } from './HeaderNavigationLink';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface HeaderNavigationMenuProps extends HeaderNavigationLinkProps {
  /**
   * Content of the menu, can be provided as children as well
   */
  menuItems?: ReactNode;
  /**
   * Props applied to menu
   */
  menuProps?: MenuProps;
  /**
   * Props applied to ClickAwayListener
   */
  clickAwayListenerProps?: Omit<ClickAwayListenerProps, 'children'>;
  /**
   * Props applied to menu on mobile
   */
  collapseProps?: CollapseProps;
}

/**
 * HeaderNavigationLink with the integration of the menu
 */
export const HeaderNavigationMenu: FC<HeaderNavigationMenuProps> = forwardRef(
  (
    {
      children,
      sx,
      label,
      icon,
      onClick,
      extend,
      menuItems,
      menuProps,
      collapseProps,
      clickAwayListenerProps,
      ...rest
    },
    ref,
  ) => {
    const buttonRef = useRef(null);
    const [anchor, setAnchor] = useState(null);
    const theme = useTheme();
    const downLg = useMediaQuery<Theme>(theme.breakpoints.down('lg'));
    const mobile = extend === undefined ? downLg : extend;
    const { sx: collapseSx, ...restCollapse } = collapseProps ?? {};

    return (
      <>
        <HeaderNavigationLink
          active={!!anchor}
          extend={extend}
          icon={
            icon || (
              <ChevronDownIcon
                sx={{
                  // same code is applied to HeaderNavigationHero.tsx
                  '&&&': {
                    fontSize: extend === undefined ? { xs: '20px', lg: '14px' } : ['20px', '14px'][+!extend],
                  },
                  color:
                    extend === undefined
                      ? {
                          xs: theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700',
                          lg: 'currentColor',
                        }
                      : [theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700', 'currentColor'][
                          +!extend
                        ],
                  transform: `rotate(${anchor ? '180' : '0'}deg)`,
                  transition:
                    extend === undefined
                      ? {
                          xs: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                          }),
                          lg: 'none',
                        }
                      : [
                          theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                          }),
                          'none',
                        ][+!extend],
                }}
              />
            )
          }
          onClick={(e) => {
            onClick?.(e);
            setAnchor((prev) => (!prev ? e.currentTarget : null));
          }}
          sx={{
            justifyContent:
              extend === undefined
                ? { xs: 'space-between', lg: 'flex-start' }
                : ['space-between', 'flex-start'][+!extend],
            ...sx,
          }}
          ref={ref ?? buttonRef}
          data-testid="HeaderNavigationMenu"
          {...rest}
        >
          {label}
        </HeaderNavigationLink>
        {mobile ? (
          <ClickAwayListener
            onClickAway={(e) => {
              !((ref as React.RefObject<HTMLButtonElement>) ?? buttonRef)?.current?.contains(e?.target as Node) &&
                setAnchor(null);
            }}
            {...clickAwayListenerProps}
          >
            <Collapse
              in={!!anchor}
              sx={{
                // style menu items inside
                '& .MuiMenuItem-root': {
                  ...getInteractiveStyle(
                    {
                      background: 'none',
                      fontWeight: '300',
                      color: theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700',
                    },
                    {
                      background: 'none',
                      fontWeight: '700',
                      color: 'currentColor',
                    },
                  ),
                },
                ...collapseSx,
              }}
              {...restCollapse}
            >
              {children || menuItems}
            </Collapse>
          </ClickAwayListener>
        ) : (
          <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} {...menuProps}>
            {children || menuItems}
          </Menu>
        )}
      </>
    );
  },
);
